import React from "react";
import { MessageSquare, DollarSign, Layers, Headset, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ReasonCard {
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const reasons: ReasonCard[] = [{
  title: "Expertos en comunicación con clientes",
  description: "No solo IA: conectamos llamadas, mensajes y sus sistemas.",
  icon: MessageSquare
}, {
  title: "Precios transparentes",
  description: "Sin cargos extra por tokens ni costos ocultos; usted sabe lo que paga.",
  icon: DollarSign
}, {
  title: "Se integra con lo que ya usa",
  description: "Su CRM, su central telefónica y sus herramientas, sin empezar de cero.",
  icon: Layers
}, {
  title: "Soporte 24/7",
  description: "Acompañamiento cuando su operación lo necesita.",
  icon: Headset
}];
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqItems: FaqItem[] = [{
  id: "item-1",
  question: "¿Tengo que cambiar mis sistemas?",
  answer: "No. Nos conectamos a lo que ya usa, como su CRM y su central telefónica, y mejoramos desde ahí."
}, {
  id: "item-2",
  question: "¿La IA reemplaza a mi equipo?",
  answer: "No. La IA responde lo repetido y fuera de horario; su equipo se dedica a los casos que necesitan una persona."
}, {
  id: "item-3",
  question: "¿Por dónde empezamos?",
  answer: "Por escucharle. Revisamos sus canales, vemos dónde pierde tiempo y dinero y empezamos por donde más recursos pierde su empresa."
}, {
  id: "item-4",
  question: "¿Cómo se cobra?",
  answer: "Con precios transparentes: sin cargos extra por tokens ni costos ocultos. En el diagnóstico le explicamos la propuesta para su caso."
}, {
  id: "item-5",
  question: "¿Qué canales puedo usar?",
  answer: "WhatsApp, chat web, llamadas y correo, todos en un solo lugar."
}];
export default function PorQueAvtecsa() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="por-que-avtecsa" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Part 1: Por qué AVTECSA */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <Badge variant="secondary" className="mb-4 uppercase tracking-wider font-semibold">
              POR QUÉ AVTECSA
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
              Los canales de comunicación son nuestro negocio
            </h2>
            <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
              AVTECSA lleva la IA a lo que sus clientes ya usan para hablar con usted: WhatsApp, llamadas, chat y correo.
            </p>
          </div>

          {/* 4 Reason Cards: 2x2 on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-16 md:mb-24">
            {reasons.map(reason => {
            const Icon = reason.icon;
            return <div key={reason.title} className="bg-card border border-border rounded-md p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:border-primary flex items-start gap-4">
                  <div className="size-12 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <Icon className="size-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold font-default text-foreground mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground font-default leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          {/* Part 2: FAQ */}
          <div className="max-w-3xl mx-auto mb-12 md:mb-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
                Preguntas frecuentes
              </h3>
            </div>

            <Accordion type="single" collapsible className="w-full border-t border-border">
              {faqItems.map(item => <AccordionItem key={item.id} value={item.id} className="border-b border-border">
                  <AccordionTrigger className="text-left font-semibold text-base md:text-lg text-foreground hover:no-underline py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm md:text-base font-default leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Centered Yellow CTA */}
          <div className="text-center">
            <Button size="lg" className="bg-chart-4 hover:bg-chart-4/90 text-black font-bold rounded-md px-8 py-6 text-base shadow-md transition-all hover:shadow-lg" asChild>
              <Link to="#contacto">
                Descubra dónde pierde dinero
                <ArrowRight className="ml-2 size-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}